define((require) => {
    "use strict";

    const EventDispatcher     = require("shared/util/EventDispatcher");
    const Promise             = require("shared/util/Promise");
    const RestApi             = require("shared/services/RestApi");
    const ViewerEvents        = require("shared/services/ViewerEvents");
    const ViewerProxy         = require("shared/ui/viewer/ViewerProxy");
    const Preconditions       = require("shared/util/Preconditions");
    const ChannelContentState = require("shared/channels/ChannelContentState");
    const {Logger}              = require("shared/util/Logger");
    const $                   = require("jquery");
    const _                   = require("underscore");

    const log = new Logger("ChannelBridge");

    /**
     * @type {Window}
     * Iframe window, not top level window!
     */
    const win = window;

    // Odd construct is to allow the bridge to be initialised once via the viewer app when the Controller is available,
    // including its region, and to avoid circular dependencies with controllers using this bridge.
    //
    let bridge = (Controller, region) => {
        // Have to run in an iframe where the channel has been primed via the channels app...
        if (win.top !== win && win.channel && Controller && region) {
            bridge = new ChannelBridge(Controller, region, win.channel);
        } else {
            bridge = null;
            log.verbose("Non channel mode detected");
        }

        if (bridge) {
            bridge._start(); // one time only!
        }

        return bridge;
    };

    // Guard to ensure the Bridge is useless if for example closed on initialisation errors...
    const ensureNotClosed = () => { // cannot use Preconditions because of circular references!
        if (!bridge || (bridge.closed && bridge.closed())) { // can still be the init function since we log stuff in the constructor
            throw new Error("Bridge/Viewer is closed");
        }
    };

    /**
     * @class ChannelBridge
     *
     * A <i>channel bridge</i> provides an interface between channels and a viewer, allowing them to notify each
     * other on events like "data preload", "content loaded", etc. It is only used when the viewer is started
     * from a channel, not for direct urls. This module will return a singleton channel bridge instance when
     * embedded via channels, otherwise null. <p>
     *
     * To determine if the viewer is running in channel mode, do:
     * <pre>
     *     ..
     *     // Imports:
     *     var channelBridge = require("ui/viewer/ChannelBridge"); // function!
     *     ..
     *
     *     // Usage in some function - always call at use time, not at import time!
     *     var bridge = channelBridge();
     *     if (bridge) {
     *         // Yes, Viewer embedded via a Channel that can be obtained via "bridge.channel()"
     *         ..
     *     } else {
     *         // No, Viewer not embedded via  Channel, do something else.
     *         ..
     *     }
     *     ..
     * </pre>
     *
     * The channel snippet will be loaded on the client domain, while this channel bridge will be loaded in the
     * embedded iframe, inserted by the channels app. The channel bridge exposes a "viewer proxy" the channel
     * can call, and the viewer will notify the channel once it has loaded content:
     *
     * <pre>
     *     // On the client site, where channels.js is included, the channels app lives in the
     *     // <tt>window.__zmags.channels</tt> sandbox, and active channels under <tt>__zmags.channels.active</tt>
     *     // See <tt>Global.sandbox()</tt> for details.
     *     ..
     *     var channel = window.__zmags.channels.active[0]; // assuming only a single active channel
     *     var viewer = channel.viewer();                   // the viewer proxy exposed by this bridge
     *     ..
     *
     *     // In the embedded iframe, the viewer proxy is exposed under <tt>window.viewer</tt>,
     *     var viewer1  = window.viewer;    // the viewer proxy, same instance as above (that is, as "viewer")
     *     var channel1 = window.channel;   // the channel already set in the iframe window, same channel as
     *                                      // above (that is, same instance as "channel")
     *     // Or get the channel like:
     *     var channel2 = viewer.channel(); // the channel, same instance as above (that is, same instance as
     *                                      // "channel" and "channel1")
     *     ..
     * </pre>
     *
     * The channels app may be configured for preloading json data, and this bridge will ensure that the viewer
     * is only fired up when said content is done loaded. On no preload, the viewer will simply be feed
     * an id for the publishable to load and it will have the load itself (akin to direct urls). <p>
     *
     * The channel bridge can also be used to add <i>embedded Experiences or Groups</i> to the current viewer
     * by creating channels on the fly pointing to such embedded content. For example:
     * <pre>
     *     ..
     *     // In some part of the Viewer code that wants to add embedded content when embedded via channels
     *     var bridge = channelBridge();
     *     if (bridge) {
     *         var options = {
     *             embedType:  "embedded",                  // type of embedding, e.g. "embedded" or "lightbox".
     *             scene:      1,                           // (actual) scene number used to generate pseudo unique but reusable channel ids.
     *             descriptor: ..,                          // url descriptor for content like {id:String, type:String, scene:Number, ..}.
     *             element:    ..,                          // jQuery element for the parent (view) element to insert the embed under.
     *             channel:    { .. },                      // standard channel options like "win", etc.
     *             callback:   function (populated) { .. }, // true on success, false on failure to populate channel.
     *         };
     *
     *         // Embed, and get the temp channel needed to remove the content again when no longer relevant,
     *         // e.g. on page navigation or close...
     *         var channel = bridge.embed(options); // see "embed(..)" function for full doc!
     *         ..
     *     } else {
     *         // optionally just insert an <iframe> with a direct url to the experience/group then
     *     }
     *     ..
     *
     *     ..
     *     // Remove again, e.g. on view close/navigation...
     *     bridge.removeEmbed(channel);
     *     ..
     * </pre>
     *
     * Since the channels app lives in client space and needs to load as fast as possible, it does not utilise
     * <tt>Promise</tt> functionality (need to support legacy browsers), but relies on client side jQuery for
     * preload. This is why the channel bridge implementation is a bit awkward to handle this, ensuring that
     * the data when passed to the viewer is wrapped in a promise promise/deferred. <p>
     *
     * For now, it works with the Creator viewer only, but could (hopefully) easily been extended to for example
     * also support the Verge viewer (Publications).
     *
     * @author Gunni Rode / <a href="http://www.zmags.com>Zmags</a>
     */
    const ChannelBridge = EventDispatcher.extend("ChannelBridge", {

        /**
         * @private @type {ErrorLogger}
         * Error logger, to ensure we wait until an error been reported before we bail out.
         */
        _errorLogger: null,
        /**
         * @private @type {ViewerProxy}
         * The Viewer Proxy used by this bridge; never null.
         */
        _viewer: null,
        /**
         * @private @type {Controller}
         * The Group or Experience controller used (also by <tt>_viewer</tt>).
         */
        _controller: null,

        /**
         * Constructor, which creates this channel bridge.
         *
         * @param {ErrorLogger} errorLogger The error logger to log errors during channel/viewer handshake; injected
         *                                  via DIA.
         * @param {ViewerProxy} viewer      The viewer proxy to use; never null.
         * @param {Channel}     channel     The channel in question; never null.
         */
        constructor(errorLogger, Controller, region, channel) {
            // Because of circular dependencies, we have to manually initialise the error logger with the channel...
            this._errorLogger = errorLogger;

            // Create controller to use...
            this._controller = new Controller({region});

            // Make sure the ViewerProxy knows it is using channels...
            this._viewer = new ViewerProxy(this._controller.state.bind(this._controller), channel); // exports itself under "viewer" (iframe window)

            let loc;
            try {
                loc = win.location.toString();
            } catch (e) { // Security error
                loc = "unknown";
            }

            // Listen for all viewer events, such as scene navigation...
            this.listenTo(ViewerEvents, "all", this._viewerEvent);

            log.info(`Created channel bridge for (channel <-> viewer) for url: '${loc}' -> ${this}`, this);
        },

        /**
         * @private
         * Notifies the channel about an event triggered by the Viewer, such as "viewer:scene:navigation" or
         * "viewer:lightbox:open", if defined on the channel proxy under <tt>event</tt>, e.g.
         * <tt>channel.callbacks.events["viewer:lightbox:open"] = function (source, experienceId, ..) { .. }</tt>. <p>
         *
         * The events triggered here has nothing to do with the Viewer loading/initialisation, but about Viewer
         * actions being triggered, initiated by user interaction such as scene navigation.
         *
         * @param {String} name      The Viewer event name, e.g. "viewer:scene:navigation"; never falsey.
         * @param {...}    [varArgs] Additional event arguments; never falsey.
         * @throws Error If this bridge/viewer is closed.
         */
        _viewerEvent(name, ...args) {
            const channel = this.channel();
            const callback = channel.callbacks.events[name];

            if (typeof callback === "function") {
                callback.apply(channel, args);
            }
        },

        /**
         * Returns true if this bridge - and thus viewer - is actually closed, false otherwise. <p>
         *
         * There is normally no need to test for this; it is only for edge cases where old IE browsers doing stuff
         * synchronously fails during Viewer initialisation, causing the viewer to be closed while it is initialising.
         * The viewer thus guards against this using this function. <p>
         *
         * @return {Boolean} True if this bridge - and thus the viewer - is closed, false otherwise.
         */
        closed() {
            return this._viewer.state("closed");
        },

        /**
         * Returns the viewer proxy used by this bridge.
         *
         * @return {*} The viewer proxy used; never null while this bridge is active.
         * @throws Error If this bridge/viewer is closed.
         */
        viewer() {
            ensureNotClosed();
            return this._viewer;
        },

        /**
         * Returns the channel proxy used by this bridge.
         *
         * @return {*} The channel proxy used; never null while this bridge is active.
         *
         * @throws Error If this bridge/viewer is closed.
         */
        channel() {
            return this.viewer().channel();
        },

        /**
         * Returns the dom element that has been matched by the channel, wrapped in a fresh jQuery object. <p>
         *
         * <tt>iframeElement()</tt> will return a (jQuery wrapped) dom object that is a child of this object.
         *
         * @return {jQuery} The matched dom element; never falsey.
         * @throws Error If this bridge/viewer is closed.
         * @see    #iframeElement
         */
        matchedElement() {
            return $(this.channel().element());
        },

        /**
         * Returns the dom iframe containing the viewer, wrapped in a fresh jQuery object. <p>
         *
         * This will be a child of <tt>matchedElement()</tt>.
         *
         * @return {jQuery} The iframe containing the viewer; never falsey.
         * @throws Error If this bridge/viewer is closed.
         * @see    #matchedElement
         */
        iframeElement() {
            return $(this.channel().container());
        },

        /**
         * Returns the possible channel cached raw json content of the specified type and id, if any.
         *
         * @param  {String} id   The content (model) id; cannot be falsey.
         * @param  {String} type The content type, e.g. "experience" or "group"; cannot be falsey.
         * @return {JSON}   The content, if any; otherwise falsey.
         * @throws Error If this bridge/viewer is closed.
         */
        content(id, type) {
            const json = this.channel().callbacks.content(id, type);
            if (json) {
                log.info(`Cached ${type} content available: '${id}'`);
            }
            return json;
        },

        /**
         * @private
         * Connects this bridge, triggering a load of the content to use by the viewer if not already.
         *
         * @return {Promise} A promise that will resolve once the initial raw json data for the viewer is ready,
         *                   as specified - and possible preloaded - by the channel used by this bridge, automatically
         *                   calling the controller with said raw json to view the content.
         */
        _start() {
            const channel   = this.channel(); // channel proxy!
            const callbacks = channel.callbacks;
            const content   = channel.content();
            const state     = content.state;

            let url;
            let promise;

            // Do we have preloaded/embedded raw json content (experience or group)? The "cache" state will be
            // on programmatically started/stopped channels or in certain embedded cases...
            if (state === ChannelContentState.LOADED || state === ChannelContentState.LOCAL || state === ChannelContentState.CACHE) {
                log.verbose(`Content preloaded: '${state}' -> ${content}`, content);

                // No fail handler required here since the content is ready...
                promise = Promise.resolved(content.data());

            // Special case: channels are currently preloading the json data via jQuery found on
            // the client page, so we create a deferred that will resolve once the channel calls
            // the viewer proxy with the loaded json content...
            } else if (state === ChannelContentState.PENDING) {
                log.verbose(`Waiting for preload to finish: ${content}`, content);
                this._viewer._deferred = Promise.deferred();

                // No fail handler required here to signal channels since the channels app already knows if this fails...
                promise = this._viewer._deferred.promise();

            // Here, the content is just a shim for the content, e.g. an experience or group id. For consistency,
            // we need to return the raw json content, so we use the sync manager for this, relying on naming
            // conventions for the REST endpoints...
            } else {
                url = `${RestApi.API_URL}/${content.type}s/${content.id()}`;

                log.verbose(`Loading content: ${content} -> '${url}'`, content);

                // Note: in IE9 (and other legacy browsers), the promises below might run synchronously, which will in
                // fact trigger the viewer to be unloaded while initialising on for example cross domain errors. This
                // means that the viewer can be destroyed during its initialisation, which causes IE9 to log many nasty
                // errors. To ensure this will not happen, this bridge is tagged as closed, but the "bridge" variable
                // not nulled. This means a (useless) reference to it can still be obtained, e.g. to ensure we
                // suddenly do not try to fire up the Viewer based on direct url since no bridge is available.
                // This is only relevant for errors as it will destroy the viewer; content loaded will be handled
                // by the relevant controller once it is fired up...

                // We use raw ajax to avoid including SyncManager in the viewer code (for now). We allow the channel to
                // cache this as well. For all other states, channels will already get an internal callback when the
                // content is ready as channels itself will have initialised the request to begin with; we thus only
                // need to add the hook here. Also note we need to specify request type, etc, to ensure this will also
                // work with IE9 cross domain requests (see XDomainRequestTransport.js and its doc)... Content-Type does
                // seem to be necessary though.
                promise = $.ajax({url, type: "GET", dataType: "json"}).done((json) => {
                    callbacks.content(json, content.type);
                    log.verbose(`Content loaded: ${content} -> '${url}'`, content);

                // Force a fail, which will also report to the error logger used, waiting for it to complete
                // before signaling channels with error. This is to avoid channels forcefully removing the
                // viewer before the error has had a chance to be reported...
                }).fail((xhr, text, error) => {
                    error = `${error} (${xhr.status})`;
                    const message = `Could not load content: ${content} -> ${error}: '${url}'`;
                    log.error(message);
                    return this._errorLogger.log(message).always(() => {
                        callbacks.error(error); // can be synchronous in IE9!
                    });
                });
            }

            // Invariant: "promise" has a primed fail handler here where it makes sense already!
            return promise.then((json) => {
                // Invariant: "json" either a group or experience! Regardless of type, it loaded from here,
                //            it will have been reported already to the channel via "content(..)", setup above
                //            in the ajax call...

                // Invariant: Backbone will mess up json it is used to create a recursive model graph by, i.e. change
                //            arrays to Backbone.Collections and what not. For this reason, channels will ensure a
                //            deep copy is cached...

                // Fire up the controller logic, where the controller can access this bridge via the ChannelBridge module...
                log.verbose(`Invoking ${content.type} controller`, json);

                // The controller will return a promise the will resolve when the viewer is fully shown, not just
                // rendered or when a group is processed, or rejected because of some viewer error. For example,
                // when an ExperienceController is used, it will resolve once the Experience is fully shown; when
                // a GroupController is used, the GroupController will determine the Experience to show, then fire
                // up an ExperienceController like above, while not resolving until the chosen Experience is fully.
                // We cannot, however, manually attach a done handler to call "notify" to notify the channel as the
                // viewer itself may use the controller to change experience, e.g. when a breakpoint is hit. The same
                // goes for failures in case nested deferreds are used. Hence, each controller must ensure that
                // "channelBridge.notify(promise:Promise)" is called (attached) as well as
                // "channelBridge.fail(promise:Promise)" where relevant...
                return this._controller.viewContent(json, content.scene.current);
            });
        },

        /**
         * Attaches "done" and "fail" handlers to the specified promise to notify this channel bridge once the
         * promise resolves the (new) Experience to show or fails in the process.
         *
         * @param {Promise} promise The promise that will resolved to the experience shown, triggering an update
         *                         to the channel; cannot be falsey.
         * @return {Promise} <tt>promise</tt>, with a done and fail handler to update the channel once the experience
         *                   is shown or to notify it of errors; never falsey.
         * @throws Error If this bridge/viewer is closed.
         */
        notify(promise) {
            const channel = this.channel();
            return this.fail(promise.done((experience) => { // "experience" is an ExperienceModel type, not json!
                log.verbose(`Experience shown for channel: '${experience.id}' -> ${channel}`, experience, channel);

                // If the experience was loaded above via the REST promise, its JSON representation is not seen by
                // channels before, so we pass it along for possible caching. If the experience was preloaded or
                // embedded, the cache will already be correctly primed, but still fine to pass the json along to
                // channels. The "shown(..)" callback will also stop spinners and what not...
                channel.callbacks.shown(experience.id, experience.toJSON());
            }));
        },

        /**
         * Attaches a fail handler to the specified promise to notify this channel bridge once the
         * promise fails to resolve; only needed for special cases, like nested deferreds.
         *
         * @param {Promise} promise The promise that will notify the channel bridge of error if rejected.
         * @return {Promise} <tt>promise</tt>, with a fail handler to update the channel if the experience fails
         *                   to be shown; never falsey.
         * @throws Error If this bridge/viewer is closed.
         */
        fail(promise, ...args) {
            const channel = this.channel();
            return promise.fail((error) => {
                const promiseArgs = [promise].concat(args);
                // We assume this error has already been reported by the viewer logic to the frontend
                // error queue, so we just log it here...
                log.error(`Viewer error for channel: ${error} -> ${channel}`, channel, promiseArgs);
                channel.callbacks.error(error); // can be synchronous in IE9!
            });
        },

        /**
         * Creates a new embedded 'in-place' channel on the fly to insert the content under the specified parent
         * element contained in the dom rendered for the content in the channel used by this channel
         * bridge. That is, creates a "child channel" of the channel used by this bridge in order to utilise
         * channel behaviour, such as allowing cross domain functionality, when embedding Experiences or Groups
         * within other Experiences. <p>
         *
         * The created channel must be removed again manually using <tt>removeEmbed</tt> when the content is no
         * longer relevant, e.g. because the user navigates to a new scene. <p>
         *
         * Note: this does <b>not</b> test for infinite recursion, like Experience A embedding Experience B
         * which embeds Experience A again! <p>
         *
         * <tt>options</tt> have the following format:
         * <pre>
         *   {
         *      descriptor: {id:String, type:String, scene:Number, ..}, - an url descriptor known to have a <tt>type</tt>
         *                                                                property of either "experience" or "group",
         *                                                                obtained via <tt>UrlUtil.getUrlDescriptor()</tt>.
         *
         *      element:    jQuery, - the parent element to use, wrapped in jQuery.
         *
         *      scene:      String, - the scene number of the scene doing the embedding, e.g. "1" or "overlay", used to
         *                            qualify embedded channels so the same content can be embedded on numerous scenes.
         *
         *      embedType:  String, - the logical embed type, e.g. "lightbox" or "embedded" (default).
         *
         *      callback:   fn(populated:Boolean, channel:Channel, descriptor:*) - callback to be invoked once the
         *                  embedded channel has been tried populated; <tt>populated</tt> will be true on success,
         *                  false otherwise. The <tt>channel</tt> argument is channel in question (also returned
         *                  by the <tt>embed(..)</tt> function), and <tt>descriptor</tt> is <tt>options.descriptor</tt>.
         *
         *      channel:    * - optional channel data/options, like  <tt>{win: Window, loadingIndicator: "spinner", ..}</tt>.
         *   }
         * </pre>
         *
         * @param  {*}       options The options to create the embedded channel by; cannot be falsey.
         *
         * @return {Channel} The 'in-place' channel proxy created for the embedded content; never falsey. The actual
         *                   dom element used can be obtained via <tt>returnedChannel.element()</tt>.
         *
         * @throws Error If this bridge/viewer is closed.
         *
         * @see    #removeEmbed
         */
        embed(options) {
            // Since we are in Viewer scope, we can afford to use jQuery, making it easy to create dom elements and
            // know when the dom is ready before adding the channel to the owning snippet...
            const channel    = this.channel().channel; // actual non-proxy channel
            const snippet    = channel.snippet;        // actual snippet
            const embedType  = options.embedType || "embedded";
            const descriptor = options.descriptor;
            // Qualify channel id with the scene of the channel backing this bridge and the scene from
            // the descriptor as well; we cannot use a sequence number since we must able to reuse the
            // channel on the scene becoming active again...
            const channelId  = `${channel.name}/${options.scene} > ${embedType} ${descriptor.type} ${descriptor.id}/${descriptor.scene}`;
            const element    = options.element[0];

            // Does such a channel already exist? If so, simply show it since it matched on an id deduced from the
            // actual content id in the format above. This ensures that we can reuse the current scene index associated
            // with the content of the channel when the user is navigating through the inner experience...
            let c = snippet.channel(channelId);
            if (c && !c.ready()) {
                c._dom.element = element; // since old one will have been destroyed when here again!
                log.info(`Re-using 'in-place' ${embedType} channel: ${c}`, c);
                // Fall-through

            // No such channel, so we create a new 'in-place' channel on the fly. We supply dom element to tag it as
            // an 'in-place' channel, but we cannot add the channel yet as said element will not be ready yet...
            } else {
                // Use these defaults, but allow them to be overridden via "options", then add immutable channel
                // options that cannot be overridden by "options"...
                c = _.extend({
                    cacheable: channel._cacheable * 10, // cache for 10 times as long as the outer channel, we are likely to come back to to when navigating through scenes
                    win                                 // make sure we use the dom from this *iframe* window for the embedded channel unless otherwise specified
                }, options.channel, {
                    name:             channelId,
                    configId:         snippet.configId,
                    url:              channel._url, // ensure the embedded channel matches the same url
                    loadingIndicator: "none",       // actually works, but issues when navigating since turned on when non-current scene is constructed
                    element,                        // dom element, not jQuery
                    embedded:         channel       // embedded via "channel" (even if the dom is the same)
                });

                // Add immutable content reference as well, e.g. {experience: {id: "xxx"}}...
                c[descriptor.type] = {id: descriptor.id};

                // Do not populate the channel yet, the dom element is not ready yet...
                snippet.addChannels(c, false);

                // Make sure we return the actually created channel, no just the raw data above...
                c = snippet.channel(channelId);

                // Update with the content with the scene to show
                c.content.scene.current = descriptor.scene;

                log.info(`Created ${embedType} channel: ${c}`, c);
            }

            // Defer to ensure the element is actually added to the dom used by the channel (associated with
            // options.channel.win or just plain on iframe window (default)...
            _.defer(() => {
                if (!this.closed()) {
                    const populated = snippet.populateChannel(c);
                    Preconditions.checkState(populated, `Channel failure for ${embedType} ${descriptor.type}: ${c}`, [descriptor, c]);
                    if (options.callback) {
                        options.callback(populated, c, descriptor);
                    }
                }
            });

            return c.proxy(); // no longer privileged access!
        },

        /**
         * Removes the content populated for the embedded 'in-place' channel, if applicable, and optionally removes
         * the channel itself.
         *
         * @param {String|Channel} channel  The id of the channel to remove or the channel proxy itself; if falsey,
         *                                  or no such channel, this function does nothing.
         * @param {Boolean}       [destroy] True to remove the channel itself, false to keep it for future re-use;
         *                                  the populated content, if any, will be removed in any case.
         *
         * @throws Error If this bridge/viewer is closed.
         *
         * @see    #embed
         */
        removeEmbed(channel, destroy) {
            const snippet = this.channel().snippet; // proxy
            if (typeof channel === "string") {
                channel = snippet.channel(channel);
            }

            if (channel) {
                log.info(`Removing embedded channel content: ${channel}`, channel);
                channel.stop(destroy);
            }
        },

        toString() {
            const content = this.closed() ? "closed" : `channel: ${this.channel()}; viewer: ${this.viewer()}`;
            return `ChannelBridge[${content}]`;
        }

    }, {
        injection: {
            parameters: ["errorLogger"]
        }
    });

    return () => bridge;
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/ChannelBridge.js